import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Users, 
  MousePointerClick, 
  Layers,
  ArrowRight,
  Check,
  Shield,
  Calendar
} from 'lucide-react';

// Use Case Card Component
const UseCaseCard = ({ icon: Icon, title, description, features }) => (
  <div className="glass rounded-2xl overflow-hidden group hover:bg-white/[0.08] transition-all">
    <div className="p-8 md:p-10">
      <div className="w-14 h-14 rounded-xl bg-indigo-500/20 flex items-center justify-center mb-6 group-hover:bg-indigo-500/30 transition-colors">
        <Icon className="w-7 h-7 text-indigo-400" strokeWidth={1.5} />
      </div>
      <h3 className="text-2xl font-semibold text-white mb-4">
        {title}
      </h3>
      <p className="text-slate-300/80 mb-6 leading-relaxed">
        {description}
      </p>
      
      <div className="space-y-3">
        {features.map((feature, i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-3 h-3 text-emerald-400" strokeWidth={3} />
            </div>
            <span className="text-sm text-slate-300/90">{feature}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default function UseCasesPage() {
  const useCases = [
    {
      icon: Building2,
      title: 'Mortgage Protection Funnels',
      description: 'Turn funnel leads into booked appointments quickly with streamlined intake and instant scheduling.',
      features: [
        'Capture mortgage protection leads with structured intake forms',
        'Route leads to available agents automatically',
        'Enable instant self-booking after form submission',
        'Track conversion from lead to appointment to sale',
      ],
    },
    {
      icon: Users,
      title: 'Referral Agent Workflows',
      description: 'Track and route referrals between agents and partners with full attribution and visibility.',
      features: [
        'Create referral intake forms for partners',
        'Automatically assign referrals to the right agent',
        'Track referral source attribution',
        'Report on referral conversion rates',
      ],
    },
    {
      icon: MousePointerClick,
      title: 'Self-Booking Lead Funnels',
      description: 'Allow prospects to schedule immediately after submitting their information for maximum conversion.',
      features: [
        'Embed one-click booking widget in funnels',
        'Prioritize same-day appointment slots',
        'Sync appointments to your calendar system',
        'Send automated confirmations and reminders',
      ],
    },
    {
      icon: Layers,
      title: 'Agency Operations',
      description: 'Centralize operational visibility across campaigns, funnels, and agents in one dashboard.',
      features: [
        'View all leads and appointments in one place',
        'Monitor agent performance and pipeline health',
        'Analyze campaign and funnel effectiveness',
        'Scale operations from solo producer to team',
      ],
    },
    {
      icon: Shield,
      title: 'Compliance Capture',
      description: 'Automatically capture and store compliance records for every lead interaction.',
      features: [
        'TrustedForm certificate capture',
        'Consent record management',
        'TCPA documentation storage',
        'Audit-ready compliance reports',
      ],
    },
    {
      icon: Calendar,
      title: 'Booking Infrastructure',
      description: 'Build reliable booking flows that convert leads into scheduled appointments.',
      features: [
        'Customizable booking widgets',
        'Calendar integration with GHL and others',
        'Smart slot management',
        'Automated reminders and follow-ups',
      ],
    },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-purple-500/15 rounded-full blur-[100px]" />
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-8">
              <span className="text-sm font-medium text-slate-300">Use Cases</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.1] mb-6">
              Built from Real Insurance Agency Workflows
            </h1>
            <p className="text-lg md:text-xl text-slate-300/80 leading-relaxed">
              MissionAssist is designed around the actual workflows insurance agencies run every day.
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases Grid */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {useCases.map((useCase, i) => (
              <UseCaseCard key={i} {...useCase} />
            ))}
          </div>
        </div>
      </section>

      {/* Additional Use Cases */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-4">
              Additional Workflows
            </h2>
            <p className="text-lg text-slate-300/80">
              MissionAssist supports a wide range of insurance agency workflows.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              'Final Expense Lead Funnels',
              'Life Insurance Lead Workflows',
              'Medicare Lead Management',
              'Health Insurance Enrollment',
              'Auto Insurance Leads',
              'Multi-Product Agency Operations',
            ].map((workflow, i) => (
              <div 
                key={i}
                className="p-5 rounded-xl bg-white/5 border border-white/10 text-center hover:bg-white/[0.08] transition-all"
              >
                <span className="text-slate-300 font-medium">{workflow}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="relative glass rounded-3xl p-12 md:p-20 overflow-hidden">
            {/* Glows */}
            <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-cyan-500/15 rounded-full blur-[80px]" />
            
            <div className="relative text-center max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-6">
                See How MissionAssist Fits Your Agency
              </h2>
              <p className="text-lg text-slate-300/80 mb-10">
                Book a demo to walk through the workflows, lead flow, and operational setup most relevant to your business.
              </p>
              
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link
                  to="/demo"
                  className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-indigo-500 text-white font-medium hover:bg-indigo-400 transition-all shadow-lg shadow-indigo-500/25"
                  data-testid="usecases-cta-demo"
                >
                  Book a Demo
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-white/5 text-white font-medium border border-white/10 hover:bg-white/10 transition-all"
                >
                  Log In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
